import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Popup } from "@progress/kendo-react-popup";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { icon, label, notifyIcon, number, quote, route } from "../../config";
import { getProjectInfo, getUserNotifEmailControl } from "../../shared/services/user.service";
import { updateUserRole, checkRemoveTeamUser, deleteProjectUser, getProjectUsers } from "../../shared/services/projects.service";
import { getNotification } from "../../utils/common";
import { filterData } from "../../utils/kendo";
import { usePopup } from "../../helper/commonHooks";
import { checkUserValidation } from "../TeamSetting/teamSetting.helper";
import EditProjectSidebar from "../UserEngagement/EngagementView/EditProjectSidebar";
import { handleUserEngagementUpdate } from "../UserEngagement/engagement.helper";
import "./Project/projectCard.scss";
import ProjectCard from "./ProjectCard";
import { userRolesOwners, teamLabels } from "./project.constants";

/**
 * ProjectDetailsPopup component displays a single project's details in a popup
 * Reuses the project card structure from Project.js
 * Parent component: Header
 * @author Sejal
 */
const ProjectDetailsPopup = ({ show, anchor, onClose, projectId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [selectedEngagementProject, setSelectedEngagementProject] = useState(null);
  const [notificationPopup, setNotificationPopup] = useState({
    show: false,
    anchor: null,
    projectId: null,
  });
  const [notificationSettings, setNotificationSettings] = useState({});
  const [userRole, setUserRole] = useState(null);
  const popupRef = useRef();
  const editSidebarRef = useRef();
  const [projectData, setProjectData] = useState(null);
  const [deletedUser, setDeletedUser] = useState(null);
  const [replacementEmployee, setReplacementEmployee] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [filterEmployeeList, setFilterEmployeeList] = useState([]);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);

  const { contentRef: profileSettingRef, blurTimeoutRef, onOpen, onFocus, onBlur } = usePopup();

  const offset = {
    left: number.FIVE_HUNDRED_EIGHTY,
    top: number.TWENTY,
  };

  if (!projectId) return null;

  /**
   * to call api on load
   */
  useEffect(() => {
    fetchProjectInfo();
  }, [projectId, show]);

  const fetchProjectInfo = async () => {
    if (projectId && show) {
      const response = await dispatch(getProjectInfo({ projectId: projectId, userId: user.id }));
      setProjectData(response);
    }
  };

  /**
   * Loads user notification settings for the project when component shows
   * @author Sejal
   */
  useEffect(() => {
    if (show && user?.id) {
      (async () => {
        const userNotificationControl = await getUserNotifEmailControl(user?.id);
        const settings = {};
        userNotificationControl?.forEach((item) => {
          settings[item.ProjectId] = {
            push: item.IsPushNotifEnabled,
            email: item.IsEmailEnabled,
          };
        });
        setNotificationSettings(settings);
      })();
    }
  }, [user?.id, show]);

  /**
   * Handles opening the engagement sidebar for a project
   * @param {Object} gridRow - The project data row
   * @author Sejal
   */
  const handleEngagementProject = (gridRow) => {
    if (!user?.JobRole) {
      getNotification(quote?.SET_UP_JOB_ROLE, notifyIcon?.WARNING_ICON);
      return;
    }

    const enrichedData = {
      ...gridRow,
      UserId: user?.id,
      Name: user?.name || user?.firstName + " " + user?.lastName || "Current User",
      Email: user?.email,
      JobRole: "", //TODO: get job role from backend
    };
    setSelectedEngagementProject(enrichedData);
  };

  /**
   * Handles saving engagement updates for a project
   * @param {Object} updatedProject - The updated project data
   * @author Sejal
   */
  const handleSaveEngagement = (updatedProject) => {
    handleUserEngagementUpdate(updatedProject, user?.id);
    setProjectData({ ...projectData, ...updatedProject });
    setSelectedEngagementProject(null);
  };

  /**
   * Handles role change for a team member
   * Updates the role in the backend and updates the local state
   */
  const handleRoleChange = (event, projectId, previousRoleId) => {
    const value = event?.target?.value["value"];
    if (value) {
      const payload = {
        entityId: projectId,
        roleId: value,
        teamMember: user?.id,
        entityType: number.THREE,
        updatedBy: user?.id,
        newRole: userRolesOwners.find((item) => item.value === value)?.label,
        previousRole: userRolesOwners.find((item) => item.value === previousRoleId.toString())?.label,
      };
      dispatch(updateUserRole(payload));
      setUserRole(userRolesOwners?.find((item) => item?.value === value?.toString()));
    }
  };

  /**
   * Validates if a user can be modified based on their role and team ownership
   * For admin users, always allow modifications
   * @author Bhavana
   */
  const validateTeamMember = (project) => {
    if (user?.isAdmin) return true;
    return checkUserValidation({
      roleId: project?.RoleId,
      projectMembersLength: project?.members,
      project,
    });
  };

  /**
   * Fetch project team members for replacement employee selection
   * @author Bhavana
   */
  useEffect(() => {
    if (deleteModal && projectId) {
      (async () => {
        const response = await dispatch(getProjectUsers({ entityId: projectId, companyId: user?.companyId }));
        const members = response?.TeamMembers || [];
        const filteredMembers = members.filter((member) => member.UserId !== deletedUser?.UserId);
        setProjectTeamMembers(filteredMembers);
        setEmployeeList(filteredMembers);
        setFilterEmployeeList(filteredMembers);
      })();
    }
  }, [deleteModal, projectId, deletedUser, user?.companyId, dispatch]);

  /**
   * Filters replacement employee list based on search input
   * @author Bhavana
   */
  const filterReplacementEmployeeChange = async (event) => {
    const filteredList = filterData(event.filter, filterEmployeeList);
    setEmployeeList([...filteredList]);
  };

  /**
   * Handles blur event for the delete modal
   * @author Bhavana
   */
  const hideOnBlur = () => {
    setDeleteModal(false);
    setReplacementEmployee(null);
    setDeletedUser(null);
  };

  /**
   * Removes a user from the project team
   * @param {Object} param - The user to remove
   * @author Bhavana
   */
  const removeUser = async (param) => {
    const removedUser = param?.UserId ?? deletedUser?.UserId;
    const userName = param?.Name ?? deletedUser?.Name;
    const payload = {
      entityId: projectId,
      deletedUser: removedUser,
      entityType: number.THREE,
      queueId: projectData?.QueueId,
      userId: user?.id,
      newUser: replacementEmployee?.UserId ? replacementEmployee.UserId : null,
    };

    try {
      setSelectedEngagementProject(null);
      await dispatch(deleteProjectUser(payload));
      setDeleteModal(false);
      setReplacementEmployee(null);
      setDeletedUser(null);

      const notificationMessage =
        removedUser === user?.id
          ? teamLabels?.REMOVED_FROM_TEAM_BY_ITSELF_SUCCESS + projectData?.ProjectName || "Team"
          : userName + teamLabels?.REMOVED_USER_BY_TEAM_OWNER_SUCCESS + projectData?.ProjectName || "Team";

      getNotification(notificationMessage, notifyIcon.SUCCESS_ICON);

      if (removedUser === user?.id) {
        onClose();
        history.push(`${route.PRIVATE_ROUTE.LANDING_PAGE.PATH}`);
      } else {
        await fetchProjectInfo();
      }
    } catch (error) {
      getNotification("Failed to remove user. Please try again.", notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles the delete action for a team member
   * Checks for dependencies before allowing deletion
   * @author Bhavana
   */
  const handleDeleteAction = async (teamMember) => {
    const userId = teamMember?.UserId || user?.id;
    if (!projectId || !userId) {
      getNotification("Invalid project or user data.", notifyIcon.ERROR_ICON);
      return;
    }

    const teamMemberData = {
      ...teamMember,
      UserId: userId,
      Name: teamMember?.Name || user?.name || "Current User",
    };

    const payload = {
      projectId: projectId,
      selectedUserId: userId,
    };

    try {
      const response = await checkRemoveTeamUser(payload);
      if (!response || !Array.isArray(response) || response?.length === number.ZERO) {
        setDeletedUser(teamMemberData);
        setDeleteModal(true);
        return;
      }

      const hasDependencies = Object.values(response[number.ZERO]).some((value) => value === number.ONE);

      setDeletedUser(teamMemberData);

      if (hasDependencies) {
        setDeleteModal(true);
      } else {
        removeUser(teamMemberData);
      }
    } catch (error) {
      setDeletedUser(teamMemberData);
      setDeleteModal(true);
      getNotification("Warning: Could not check dependencies. Please select a replacement user.", notifyIcon.WARNING_ICON);
    }
  };

  const notificationState = { notificationPopup, setNotificationPopup };

  /**
   * Renders the delete confirmation modal
   * @author Bhavana
   */
  const renderDeleteModal = () => (
    <Popup
      offset={offset}
      show={deleteModal}
      onOpen={() => onOpen(profileSettingRef)}
      animate={false}
      id='team-remove-user-popup'
      popupClass={"dt-popup"}>
      <div
        className='remover-user-popup'
        tabIndex={number.ZERO}
        onFocus={() => onFocus(blurTimeoutRef)}
        onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}
        ref={profileSettingRef}>
        <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
          <div className='dt-popup-title'>{label.REMOVE_USER_HEADER}</div>
          <span className='dt-popup-cancel-btn' title={label.CLOSE_POPUP} onClick={hideOnBlur}>
            {icon.CLOSE}
          </span>
        </div>
        <div className='dt-popup-body'>
          <div>
            {deletedUser?.UserId !== user?.id ? (
              <h6>{teamLabels.CONFIRMATION_HEADING_FOR_OWNER}</h6>
            ) : (
              <h6>{teamLabels.CONFIRMATION_HEADING_FOR_USER + projectData?.ProjectName + " ?"}</h6>
            )}
            <div className='d-flex justify-content-between mt-2'>
              <div className='fs-2 mt-2 mr-2 grey'>{teamLabels.CONFIRMATION_MSG}</div>
              <span>
                <DropDownList
                  id='team-replacement-user'
                  className='remover-user-dropdown'
                  data={employeeList}
                  value={replacementEmployee ? replacementEmployee : { UserId: null, Name: label.UNASSIGNED }}
                  textField='Name'
                  dataItemKey='UserId'
                  name='replacementEmployee'
                  onChange={(e) => setReplacementEmployee(e.target.value)}
                  filterable={true}
                  onFilterChange={filterReplacementEmployeeChange}
                />
              </span>
            </div>
          </div>
          <div className='mt-4 text-right' id='profile-update-btn'></div>
        </div>
        <div className='dt-popup-footer text-right'>
          <button onClick={hideOnBlur} id='team-remove-user-popup-cancel' className='btn-width btn btn-ghost mr-2'>
            {label.CANCEL}
          </button>
          <button
            onClick={() => removeUser()}
            id='team-remove-user-popup-confirm'
            className='btn-width btn btn-primary'
            disabled={!replacementEmployee || replacementEmployee?.Name === label.UNASSIGNED}>
            {label.YES}
          </button>
        </div>
      </div>
    </Popup>
  );

  /**
   * Mark document when sidebar is open so parent handlers can detect it
   */
  useEffect(() => {
    if (selectedEngagementProject) {
      document.body.setAttribute('data-project-sidebar-open', 'true');
    } else {
      document.body.removeAttribute('data-project-sidebar-open');
    }

    return () => {
      document.body.removeAttribute('data-project-sidebar-open');
    };
  }, [selectedEngagementProject]);

  /**
   * Clear sidebar when popup is closed
   * @author Sejal
   */
  useEffect(() => {
    if (!show && selectedEngagementProject) {
      setSelectedEngagementProject(null);
    }
  }, [show, selectedEngagementProject]);

  /**
   * Handles popup close - delegates to parent's onClose which will close everything
   * @author Sejal
   */
  const handlePopupClose = (event) => {
    onClose();
  };

  /**
   * Handles close from ProjectCard actions (like navigation to tasks, settings, etc.)
   * @author Sejal
   */
  const handleProjectCardClose = () => {
    onClose();
  };

  return (
    <>
      <Popup
        show={show}
        onClose={handlePopupClose}
        anchor={anchor?.current}
        anchorAlign={{ vertical: "bottom", horizontal: "left" }}
        popupAlign={{ vertical: "top", horizontal: "left" }}
        animate={true}
        popupClass='project-details-popup-positioned'>
        <div ref={popupRef} className='project-details-popup-content'>
          {projectData && (
            <ProjectCard
              projectData={projectData}
              isPopupMode={true}
              onClose={handleProjectCardClose}
              notificationSettings={notificationSettings}
              setNotificationSettings={setNotificationSettings}
              onEngagementEdit={handleEngagementProject}
              notificationState={notificationState}
            />
          )}
        </div>
      </Popup>

      {selectedEngagementProject && (
        <div ref={editSidebarRef} className='edit-project-sidebar'>
          <EditProjectSidebar
            userRole={userRole}
            onRoleChange={handleRoleChange}
            setUserRole={setUserRole}
            project={selectedEngagementProject}
            onSave={handleSaveEngagement}
            onCancel={() => setSelectedEngagementProject(null)}
            projectData={[projectData]}
            setProjectData={setProjectData}
            isTeamMember={true}
            handleDeleteAction={handleDeleteAction}
            ProjectId={projectId}
            checkUserValidation={validateTeamMember(selectedEngagementProject)}
            disableClickOutside={true}
          />
        </div>
      )}

      {renderDeleteModal()}
    </>
  );
};

export default ProjectDetailsPopup;
